import * as Sentry from '@sentry/nextjs';
import { GetStaticPathsContext, GetStaticPropsContext, InferGetStaticPropsType } from 'next';
import { PHASE_DEVELOPMENT_SERVER, PHASE_PRODUCTION_BUILD } from 'next/constants';
import { useTranslation } from 'next-i18next';

import { category as categoryService } from '@boss/services';
import type { StaticPaths } from '@boss/types/b2b-b2c';
import { toSentenceCase } from '@boss/utils';

import { InstantSearch, SEO, mapPromotionCard } from '../../components';
import { FACET_FILTERS_BASE, PageNames, PageTypes, SEARCH_ATTRIBUTES_OBJECT } from '../../constants';
import { CategoryPage } from '../../containers';
import Layout from '../../layout/default';
import { buildCategoryBreadcrumb, getProductSearchIndexName } from '../../utils';
import getServerSideTranslations from '../../utils/getServerSideTranslations';
import { mapNextToContentfulLocaleMarket } from '../../utils/localeMapper';
import { getMappedNavigation } from '../../utils/navigation';
import { getBreadcrumbVisibilityForPage } from '../../utils/options';

const BATCH_MODE =
  process.env.NEXT_PHASE === PHASE_PRODUCTION_BUILD ||
  (process.env.NEXT_PHASE === PHASE_DEVELOPMENT_SERVER && process.env.DEBUG_BUILD === 'true');

export const getStaticPaths = async ({ locales }: GetStaticPathsContext) => {
  if (!locales) {
    return { notFound: true };
  }

  try {
    const filteredLocales = locales?.filter(locale => locale !== 'default');

    const paths: StaticPaths = [];
    const promises = filteredLocales.map(
      async (locale: string) =>
        await categoryService.getAll(mapNextToContentfulLocaleMarket(locale), categoryService.DEPTH_LEVEL2, BATCH_MODE),
    );
    const categoryLists = await Promise.all(promises);

    filteredLocales.forEach((locale, index) => {
      const categoryList = categoryLists[index];

      if (categoryList) {
        categoryList.forEach(category => {
          // remove the /c part of the uri to find the appropriate slugs, e.g. /c/verf/grondlagen
          const slugs = category.uris[mapNextToContentfulLocaleMarket(locale)].replace('/c/', '').split('/');

          paths.push({ locale, params: { slug: slugs } });
        });
      }
    });

    return {
      paths,
      fallback: false,
    };
  } catch (e) {
    Sentry.captureException(e, {
      tags: {
        type: 'Fetching Category paths',
      },
    });
  }

  return {
    paths: [],
    fallback: 'blocking',
  };
};

export const getStaticProps = async ({
  preview,
  params,
  locale,
}: GetStaticPropsContext<{ slug: string[]; locale: string }>) => {
  if (!locale || !params || locale === 'default') {
    return {
      notFound: true,
    };
  }

  const contentfulLocaleMarket = mapNextToContentfulLocaleMarket(locale);
  const slugs = params.slug;
  const categoryPath = slugs.join(' > ');
  const categoryType = `l${slugs.length}`;
  const facetFilter = `${FACET_FILTERS_BASE} AND categorySlugs.l${slugs.length}:"${categoryPath}"`;

  const attributes = [SEARCH_ATTRIBUTES_OBJECT[`l${slugs.length + 1}`]];

  const [navigation, category] = await Promise.all([
    getMappedNavigation(contentfulLocaleMarket, !!preview),
    categoryService.getBySlug(slugs.join('/'), contentfulLocaleMarket, BATCH_MODE),
  ]);

  if (!category) {
    return {
      notFound: true,
    };
  }

  const promotion = await categoryService.getPromotion(category.id, contentfulLocaleMarket);

  return {
    props: {
      pageType: PageTypes.CATEGORY_DETAIL,
      title: category.description,
      preview: !!preview,
      description: category.seoDescription ?? category.description,
      promotion,
      navigation,
      locale,
      facetFilter,
      attributes,
      categoryType,
      localizedSlugs: category.uris,
      slugs,
      suffixSections: category.suffixSections ?? null,
      showBanner: category.showBanner ?? null,
      ...(await getServerSideTranslations(locale, ['common', 'facets', 'forms'])),
    },
  };
};
export const Category = ({
  title,
  promotion,
  navigation,
  locale,
  facetFilter,
  attributes,
  categoryType,
  slugs,
  localizedSlugs,
  suffixSections,
  preview,
  showBanner,
  description,
}: InferGetStaticPropsType<typeof getStaticProps>) => {
  const { t } = useTranslation();

  const indexName = getProductSearchIndexName(locale);
  const categoryCrumbles = getBreadcrumbVisibilityForPage(PageNames.MAIN_CATEGORY_OVERVIEW)
    ? [{ href: '/c', label: t('overview') }].concat(buildCategoryBreadcrumb(slugs))
    : buildCategoryBreadcrumb(slugs);

  const category = categoryCrumbles[categoryCrumbles.length - 1].label;

  const pageTitle = category ? toSentenceCase(category) : '';

  return (
    <SEO
      breadcrumbs={categoryCrumbles}
      description={description}
      localizedSlugs={localizedSlugs}
      navigation={navigation}
      title={pageTitle}
    >
      <Layout
        childrenClassName="min-h-[calc(100vh-15rem)]" // Min-height to avoid collapsed grid on initial render due to InstantSearch client side search
        crumbles={categoryCrumbles}
        localizedSlugs={localizedSlugs}
        navigation={navigation}
        preview={preview}
      >
        <InstantSearch facetFilters={facetFilter} indexName={indexName}>
          <CategoryPage
            attributes={attributes}
            categoryType={categoryType}
            indexName={indexName}
            promotion={promotion ? mapPromotionCard(promotion, locale) : undefined}
            showBanner={showBanner ?? undefined}
            suffixSections={suffixSections}
            title={title}
          />
        </InstantSearch>
      </Layout>
    </SEO>
  );
};

export default Category;
